import { ResourceHeader } from '@factorialco/factorial-one-react/dist/experimental'
import { Ai } from '@factorialco/factorial-one-react/icons/app'
import {
  EmployeeMeetingHeaderFragment,
  OneOnOneMeetingGenerateSummaryDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI, isBackendDateToday } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  meeting: EmployeeMeetingHeaderFragment
  canGenerateSummary: boolean
}

export function EmployeeMeetingHeader({ meeting, canGenerateSummary }: Props) {
  const { employee } = useCurrentSession()
  const [generateSummary, { loading: generatingSummary }] = useMutation(
    OneOnOneMeetingGenerateSummaryDocument,
    {
      extractorFn: (data) => data.oneOnOnesMutations.augmentWithAiOneononesMeeting,
    }
  )

  const participant = [meeting.oneOnOne.firstParticipant, meeting.oneOnOne.secondParticipant].find(
    (p) => p?.id === employee?.id
  )
  if (!participant) {
    return null
  }

  const startDate = dateBackendDateTimeToUI(meeting.startTime)
  const isActive = isBackendDateToday(meeting.startTime)
  const showGenerateSummary = canGenerateSummary && !meeting.talkingPoints?.at(0)?.summary

  return (
    <ResourceHeader
      title={participant?.fullName || i18n.t('one_on_ones.meeting.unknown_employee')}
      description={participant?.referenceContract?.jobCatalogRole || undefined}
      avatar={{
        type: 'person',
        firstName: participant?.firstName || i18n.t('one_on_ones.meeting.unknown_employee'),
        lastName: participant?.lastName || '',
        src: participant?.avatar ? buildUrl(participant.avatar.url) : undefined,
      }}
      primaryAction={
        showGenerateSummary
          ? {
              label: i18n.t('one_on_ones.meeting.summary.generate'),
              icon: Ai,
              disabled: generatingSummary,
              onClick: async () => {
                const response = await generateSummary({
                  variables: { meetingId: meeting.id, authorId: Number(employee?.id) },
                })

                return handleResult(response, () => null)
              },
            }
          : undefined
      }
      status={
        isActive
          ? {
              label: i18n.t('one_on_ones.meeting.status'),
              text: i18n.t('one_on_ones.meeting.active'),
              variant: 'positive',
            }
          : undefined
      }
      metadata={[
        {
          label: i18n.t('common.date_date'),
          value: { type: 'text', content: startDate },
        },
      ]}
    />
  )
}
